import { createContext, useContext } from 'react';

/**
 * The screens every app opens before a session, the paths they answer on, and the nursery's slug as an entry
 * path (Q64, decision 2026-10-14 #2).
 *
 * A nursery's link is `guardian.daghfal.org/nuvakids`: the slug is the first part of the path, and the
 * screens a link can lead to answer under it (`/nuvakids/sign-in`, `/nuvakids/invitation?token=…`), with the
 * nursery's name and logo. Only two-part paths: a top-level `/:slug` would outrank the app's own `/*` and take
 * `/account` for a slug. A bare `/nuvakids` is sent on by `RequireSession` (signed out) or `EntryOrNotFound`
 * (signed in). The platform console has no slug.
 *
 * Every first segment an app routes is reserved on the server, so no slug can collide with one
 * (`TenantSlugRules`, `TenantSlugRulesTests`).
 */
export type EntryScreen =
  | 'sign-in' | 'choose' | 'invitation' | 'forgot-password' | 'confirm-phone' | 'recover' | 'privacy' | 'terms';

export interface EntryPath {
  /** The path as the router declares it. */
  path: string;
  screen: EntryScreen;
  /** Whether it is the slug-prefixed form, which only the three nursery apps declare. */
  slug: boolean;
}

export const ENTRY_PATHS: readonly EntryPath[] = [
  { path: '/sign-in', screen: 'sign-in', slug: false },
  { path: '/choose', screen: 'choose', slug: false },
  { path: '/invitation', screen: 'invitation', slug: false },
  { path: '/forgot-password', screen: 'forgot-password', slug: false },
  { path: '/confirm-phone', screen: 'confirm-phone', slug: false },
  { path: '/recover', screen: 'recover', slug: false },
  // The privacy notice and the terms (Q67): open to anyone, signed in or not, in every app.
  { path: '/privacy', screen: 'privacy', slug: false },
  { path: '/terms', screen: 'terms', slug: false },
  { path: '/:slug/sign-in', screen: 'sign-in', slug: true },
  { path: '/:slug/invitation', screen: 'invitation', slug: true },
  { path: '/:slug/forgot-password', screen: 'forgot-password', slug: true },
  { path: '/:slug/confirm-phone', screen: 'confirm-phone', slug: true },
  { path: '/:slug/recover', screen: 'recover', slug: true },
];

/** The server's `TenantSlugRules` shape: lower-case letters, digits and inner hyphens, 1 to 64 long. */
const SLUG_SHAPE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/** Whether a path segment could be a slug. It may still name nothing. */
export function hasSlugShape(segment: string): boolean {
  return SLUG_SHAPE.test(segment);
}

/** The path of an entry screen, under the nursery's slug where there is one. */
export function entryPath(screen: EntryScreen, slug: string | null | undefined): string {
  return slug ? `/${slug}/${screen}` : `/${screen}`;
}

/** The single segment of a path like `/nuvakids`, where it has a slug's shape; null otherwise. */
export function slugOfBarePath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length === 1 && hasSlugShape(segments[0]!) ? segments[0]! : null;
}

/** The slug of the entry link the current screen was opened under; null on the bare host. */
export const EntrySlugContext = createContext<string | null>(null);

export function useEntrySlug(): string | null {
  return useContext(EntrySlugContext);
}
